'use client';

import { ShowMore } from '@ria-client/tw-shared-ui';
import { useState } from 'react';
import type { NodeProps } from '../types';

export interface TableOfContentsEntry {
  target: string;
  textContent: string;
}

export type TableOfContentsProps = NodeProps<{
  title: string;
  hasItemMarker?: boolean;
  hasNumberPrefix?: boolean;
  showMore?: boolean;
  numItemsAboveFold?: number;
  items?: TableOfContentsEntry[];
  /** Article right rail (legacy TableOfContentsNested): fixed-height scroll list. */
  isTocOnRightRail?: boolean;
}>;

export function TableOfContents({ props }: TableOfContentsProps) {
  const {
    title,
    hasItemMarker = false,
    hasNumberPrefix = false,
    showMore = true,
    numItemsAboveFold = 5,
    items = [],
    isTocOnRightRail = false,
  } = props;
  const [activeItemIndex, setActiveItemIndex] = useState(-1);

  if (items.length === 0) return null;

  const List = hasNumberPrefix ? 'ol' : 'ul';
  const entries = (
    <List className="m-0 list-none p-0">
      {items.map((item, index) => {
        const isActive = hasItemMarker && index === activeItemIndex;

        return (
          <li className="m-0 p-0" key={item.target}>
            <a
              className="block w-full text-secondary-foreground no-underline hover:underline"
              href={`#${item.target}`}
              onClick={() => setActiveItemIndex(index)}
            >
              <span
                className={`flex cursor-pointer flex-col border-l-2 py-2 pl-4 sm:hover:bg-secondary ${isActive ? 'border-[var(--content-blue-lighter)] bg-secondary' : 'border-border bg-transparent hover:border-[var(--content-blue-lighter)]'} active:border-secondary-foreground`}
                data-active={isActive}
              >
                {hasNumberPrefix ? <span>{index + 1}.</span> : null}
                <span className={isActive ? 'font-bold' : 'font-normal'}>{item.textContent}</span>
              </span>
            </a>
          </li>
        );
      })}
    </List>
  );

  return (
    <div className="my-6">
      <p className="m-0 font-sans text-base leading-6 font-bold uppercase">{title}</p>
      <div className="mt-4">
        {showMore ? (
          <ShowMore
            hideBottomGradient
            maxContentHeight={numItemsAboveFold * 40}
            showLessText="Hide"
            showMoreText="Show More"
          >
            {entries}
          </ShowMore>
        ) : isTocOnRightRail ? (
          // 448px: legacy table-of-contents-nested.module.less scroll list max-height.
          <div className="max-h-[448px] overflow-y-auto pr-4">{entries}</div>
        ) : (
          entries
        )}
      </div>
    </div>
  );
}
